import { QueryClient, QueryFunction } from "@tanstack/react-query";

// Global handler for 401 errors (token expiration)
let onUnauthorizedCallback: (() => void) | null = null;

export function setUnauthorizedHandler(callback: () => void) {
  onUnauthorizedCallback = callback;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // Handle 401 Unauthorized - token expired or invalid
    if (res.status === 401 && onUnauthorizedCallback) {
      console.log("🔒 Token expired or invalid - logging out user");
      onUnauthorizedCallback();
    }

    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorData = await res.json();
      console.log("API Error Data:", errorData); // Debug log

      // Create error with all properties from server response
      const error = new Error(errorData.message || res.statusText) as any;
      error.type = errorData.type;
      error.status = res.status;
      error.statusText = res.statusText;

      // Copy all other properties from errorData
      Object.keys(errorData).forEach(key => {
        if (key !== 'message') {
          error[key] = errorData[key];
        }
      });

      console.log("Throwing error with type:", error.type); // Debug log
      throw error;
    } else {
      const text = (await res.text()) || res.statusText;
      throw new Error(`${res.status}: ${text}`);
    }
  }
}

const API_BASE_URL = 'https://app.padelo.fr';

function isDevelopment(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function isNativePlatform(): boolean {
  return typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.() === true;
}

function safeGetLocalStorage(key: string): string | null {
  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to access localStorage for key "${key}":`, error);
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string): void {
  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set localStorage for key "${key}":`, error);
  }
}

export function resolveUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const cleanUrl = url.startsWith('/') ? url : `/${url}`;

  if (isNativePlatform()) {
    return `${API_BASE_URL}${cleanUrl}`;
  }

  if (isDevelopment()) {
    return cleanUrl;
  }

  return `${API_BASE_URL}${cleanUrl}`;
}

async function fetchRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  headers?: Record<string, string>,
): Promise<Response> {
  const requestHeaders: Record<string, string> = { ...(headers ?? {}) };

  if (data && !requestHeaders['Content-Type']) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const token = safeGetLocalStorage('authToken');
  if (token && !requestHeaders['Authorization']) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  const finalUrl = resolveUrl(url);
  const platform = isNativePlatform() ? 'NATIVE' : isDevelopment() ? 'WEB-DEV' : 'WEB-PROD';
  console.log(`[API Request] ${platform} ${method} ${url} -> ${finalUrl}`);

  try {
    const res = await fetch(finalUrl, {
      method,
      headers: requestHeaders,
      body: data ? JSON.stringify(data) : undefined,
    });

    await throwIfResNotOk(res);
    return res;
  } catch (error: any) {
    // Handle network errors (offline, server down)
    console.error(`[API Request Error] ${method} ${url}:`, error);

    // Check if it's a connection error (TypeError commonly indicates fetch failure like ECONNREFUSED)
    // or specifically check for known error messages if possible
    if (error.name === 'TypeError' || error.message.includes('Failed to fetch') || error.message.includes('Network request failed')) {
      console.log('🔴 Server appears to be unavailable');
      window.dispatchEvent(new CustomEvent('server-unavailable'));
    }

    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  headers?: Record<string, string>,
): Promise<Response> {
  return fetchRequest(method, url, data, headers);
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
    async ({ queryKey }) => {
      // Automatically add JWT token if available
      const token = safeGetLocalStorage('authToken');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const url = queryKey.join("/") as string;
      const res = await fetchRequest('GET', url, undefined, headers);

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
